import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Card, Button, Pill, PillRow, Badge, StatusRing, Avatar } from "../shared";
import { CustomIcon } from "../shared/CustomIcon";
import { fullName } from "../../lib/format";

// =============================================================
// AdminTreeView — Phase 6 reorganised admin surface for the
// Follow-Up Tree. Sticky header + search + level pills + quick
// stats + collapsible level groups + admin tool panel.
//
// This is the only surface that owns the admin-view UI; My Team
// View, Full Tree canvas, Drilldown, and the God node display
// remain untouched.
//
// Contract (props):
//   nodesArr             : array of every non-God node
//   nodesByLevel         : dictionary { levelNumber: [nodes...] }
//   members              : full member roster
//   isSuperAdmin,
//   isLimitedAdmin       : role gates
//   orphaned, delReqs    : orphan + deletion-request counts
//   healthMap            : per-node health dot info (unused here)
//   onAssign, onRemove, onReassign, onAddChild, onViewLog,
//   onRequestDelete      : all delegate to the parent page,
//                          which already owns the modal state.
//   onOpenSearchPanel,
//   onOpenHealthPanel,
//   onOpenReassignPanel,
//   onOpenDeletionPanel  : open the four bottom admin tool panels
// =============================================================

// Map node.status → Card variant + display token
const STATUS_TO_VARIANT = {
  Occupied:    "green",
  Vacant:      "amber",
  Inactive:    "grey",
  Deceased:    "grey",
  Transferred: "grey",
  Removed:     "grey",
  DeletionRequested: "red",
};

// Simple chevron-down SVG — rotated 180° via CSS when the group is expanded.
function Chevron({ size = 12 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M2 4.5l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Highlight matched substring inside a title
function HighlightedText({ text, term }) {
  if (!term || !text) return <>{text}</>;
  const idx = String(text).toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: "var(--kc-accent-bg)",
          color: "var(--kc-accent)",
          padding: "0 2px",
          borderRadius: 2,
        }}
      >
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>
  );
}

// -------------------------------------------------------------
// LevelGroup — one collapsible section per Greek level.
// -------------------------------------------------------------
function LevelGroup({
  levelKey,
  nodes,
  expanded,
  onToggle,
  renderCard,
  headerRef,
}) {
  const levelName = nodes[0]?.levelName || `Level ${levelKey}`;
  return (
    <section
      data-testid={`tree-level-${levelKey}`}
      style={{
        border: "1px solid var(--kc-border)",
        borderRadius: "var(--kc-radius)",
        overflow: "hidden",
        background: "var(--kc-surface)",
      }}
    >
      <button
        ref={headerRef}
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`tree-level-header-${levelKey}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-sm)",
          width: "100%",
          padding: "var(--space-sm) var(--space-md)",
          background: "var(--kc-elevated)",
          border: "none",
          cursor: "pointer",
          color: "inherit",
        }}
      >
        <span
          className="text-section-header"
          style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-sm)" }}
        >
          Level {levelKey} · {levelName}
          <Badge variant="blue" data-testid={`tree-level-count-${levelKey}`}>{nodes.length}</Badge>
        </span>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.25s ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            color: "var(--kc-accent)",
          }}
        >
          <Chevron size={14} />
        </span>
      </button>
      <div
        style={{
          maxHeight: expanded ? 4000 : 0,
          overflow: "hidden",
          transition: "max-height 0.25s ease",
        }}
      >
        <div
          style={{
            padding: "var(--space-md)",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "var(--space-sm)",
          }}
        >
          {nodes.map(renderCard)}
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------
// TreeNodeCard — semantic Card for one slot in the admin view.
// -------------------------------------------------------------
function TreeNodeCard({
  node, members, term, isSuperAdmin, isLimitedAdmin, hasPendingDeletion,
  onAssign, onRemove, onReassign, onAddChild, onViewLog, onRequestDelete,
}) {
  const member = node.memberId ? (members || []).find(m => m.id === node.memberId) : null;
  const isVacant = !member;
  const variant = hasPendingDeletion
    ? "red"
    : (STATUS_TO_VARIANT[node.status] || (isVacant ? "amber" : "green"));
  const memberLabel = member
    ? fullName(member)
    : (node.status === "Vacant" ? "Vacant slot" : `${node.status}`);
  const canAddChild = (node.childNodeIds || []).length < 3 && node.level < 23;
  const isAdmin = isSuperAdmin || isLimitedAdmin;

  return (
    <Card
      variant={variant}
      data-testid={`node-card-${node.nodeId}`}
      data-status={node.status}
      style={{
        padding: "var(--space-md)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-sm)",
        borderStyle: isVacant ? "dashed" : "solid",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-sm)" }}>
        <StatusRing status={member?.memberStatus || "Vacant"} size={44}>
          <Avatar
            name={member ? fullName(member) : "?"}
            photo={member?.photo}
            memberId={member?.id}
            status={member?.memberStatus}
            noRing
            style={{ width: 40, height: 40, fontSize: 12 }}
          />
        </StatusRing>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-eyebrow" data-testid={`node-slot-${node.nodeId}`}>
            <HighlightedText text={node.slotRef} term={term} />
          </div>
          <div className="text-card-primary" style={{ marginTop: 2, wordBreak: "break-word" }}>
            <HighlightedText text={memberLabel} term={term} />
          </div>
          {member?.memberNumber && (
            <div className="text-meta" style={{ color: "var(--kc-accent)" }}>
              <HighlightedText text={`#${member.memberNumber}`} term={term} />
            </div>
          )}
          <div
            className="flex flex-wrap"
            style={{ gap: 4, marginTop: 6, alignItems: "center" }}
          >
            <Badge variant="blue" data-testid={`node-level-badge-${node.nodeId}`}>
              {node.levelName || `L${node.level}`}
            </Badge>
            <Badge
              variant={hasPendingDeletion ? "red" : (variant === "green" ? "green" : variant === "amber" ? "amber" : "grey")}
              data-testid={`node-status-badge-${node.nodeId}`}
            >
              {hasPendingDeletion ? "Deletion Requested" : node.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Action buttons — tertiary to reduce visual noise. Assign uses secondary. */}
      {isAdmin && (
        <div className="flex flex-wrap" style={{ gap: "var(--space-xs)", marginTop: "var(--space-xs)" }}>
          {isVacant ? (
            <Button
              variant="secondary"
              size="sm"
              data-testid={`node-assign-${node.nodeId}`}
              onClick={() => onAssign?.(node)}
            >
              Assign Member
            </Button>
          ) : (
            <Button
              variant="tertiary"
              size="sm"
              data-testid={`node-remove-${node.nodeId}`}
              onClick={() => onRemove?.(node)}
            >
              Remove
            </Button>
          )}
          {!isVacant && (
            <Button
              variant="tertiary"
              size="sm"
              data-testid={`node-reassign-${node.nodeId}`}
              onClick={() => onReassign?.(node)}
            >
              Reassign
            </Button>
          )}
          <Button
            variant="tertiary"
            size="sm"
            data-testid={`node-log-${node.nodeId}`}
            onClick={() => onViewLog?.(node)}
          >
            Log
          </Button>
          {canAddChild && (
            <Button
              variant="tertiary"
              size="sm"
              data-testid={`node-add-child-${node.nodeId}`}
              onClick={() => onAddChild?.(node)}
            >
              Add Child
            </Button>
          )}
          {isSuperAdmin && !hasPendingDeletion && (
            <Button
              variant="tertiary"
              size="sm"
              data-testid={`node-request-delete-${node.nodeId}`}
              onClick={() => onRequestDelete?.(node)}
            >
              Request Delete
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------
// AdminTreeView (main export)
// -------------------------------------------------------------
export default function AdminTreeView({
  nodesArr,
  nodesByLevel,
  members,
  currentUser,
  isSuperAdmin,
  isLimitedAdmin,
  orphaned = [],
  delReqs = [],
  onAssign, onRemove, onReassign, onAddChild, onViewLog, onRequestDelete,
  onSwitchToMyTeam,
  onOpenFullTree,
  onSwitchToDrilldown,
  onOpenSearchPanel,
  onOpenHealthPanel,
  onOpenReassignPanel,
  onOpenDeletionPanel,
}) {
  // ---------- state ----------
  const [term, setTerm] = useState("");
  const [statFilter, setStatFilter] = useState(null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  // Expanded map keyed by level number. Beta (level 1) is expanded by default.
  const [expanded, setExpanded] = useState(() => ({ 1: true }));
  // Snapshot of expansion state when the user starts typing — restored on clear.
  const preSearchExpand = useRef(null);
  const groupRefs = useRef({});

  // Deletion-request lookup by nodeId → true
  const delReqsByNode = useMemo(() => {
    const map = {};
    (delReqs || []).forEach(d => {
      if (d.entityId && d.entityType === "node") map[d.entityId] = true;
    });
    return map;
  }, [delReqs]);
  const orphanedByNode = useMemo(() => {
    const map = {};
    (orphaned || []).forEach(o => { if (o.nodeId) map[o.nodeId] = true; });
    return map;
  }, [orphaned]);

  // Available levels (only those with at least one node)
  const availableLevels = useMemo(
    () => Object.keys(nodesByLevel).sort((a, b) => Number(a) - Number(b)),
    [nodesByLevel],
  );

  // ---------- quick stats ----------
  const stats = useMemo(() => {
    let total = 0, occupied = 0, vacant = 0;
    nodesArr.forEach(n => {
      total += 1;
      if (n.status === "Occupied") occupied += 1;
      else if (n.status === "Vacant") vacant += 1;
    });
    return {
      total,
      occupied,
      vacant,
      reassign: orphaned.length,
      deletion: (delReqs || []).filter(d => d.entityType === "node").length,
    };
  }, [nodesArr, orphaned, delReqs]);

  // ---------- filtering ----------
  const trimmed = term.trim().toLowerCase();
  const filterFn = useCallback((n) => {
    // Stat filter first
    if (statFilter === "occupied" && n.status !== "Occupied") return false;
    if (statFilter === "vacant" && n.status !== "Vacant") return false;
    if (statFilter === "reassign" && !orphanedByNode[n.nodeId]) return false;
    if (statFilter === "deletion" && !delReqsByNode[n.nodeId]) return false;
    // Text term
    if (!trimmed) return true;
    const member = n.memberId ? (members || []).find(m => m.id === n.memberId) : null;
    const hay = [
      n.slotRef,
      n.levelName,
      member ? fullName(member) : "",
      member?.memberNumber ? `#${member.memberNumber}` : "",
    ].join(" ").toLowerCase();
    return hay.includes(trimmed);
  }, [statFilter, orphanedByNode, delReqsByNode, trimmed, members]);

  // Filtered node lists per level
  const filteredByLevel = useMemo(() => {
    const out = {};
    availableLevels.forEach(lvl => {
      const arr = nodesByLevel[lvl].filter(filterFn);
      if (arr.length > 0) out[lvl] = arr;
    });
    return out;
  }, [availableLevels, nodesByLevel, filterFn]);

  // Total match count for the "Showing X of Y" line
  const filteredCount = useMemo(
    () => Object.values(filteredByLevel).reduce((s, a) => s + a.length, 0),
    [filteredByLevel],
  );

  // When search starts, snapshot expansion then expand all groups.
  // When search clears, restore the snapshot.
  useEffect(() => {
    if (trimmed && preSearchExpand.current == null) {
      preSearchExpand.current = { ...expanded };
      const all = {};
      availableLevels.forEach(l => { all[l] = true; });
      setExpanded(all);
    } else if (!trimmed && preSearchExpand.current != null) {
      setExpanded(preSearchExpand.current);
      preSearchExpand.current = null;
    }
  }, [trimmed, availableLevels]);

  // Tap a level pill: expand only that level, collapse others, scroll header.
  const focusLevel = (lvl) => {
    const next = {};
    availableLevels.forEach(l => { next[l] = String(l) === String(lvl); });
    setExpanded(next);
    // Defer scroll until the transition kicks in.
    requestAnimationFrame(() => {
      const el = groupRefs.current[lvl];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const toggleLevel = (lvl) => {
    setExpanded(prev => ({ ...prev, [lvl]: !prev[lvl] }));
  };

  const toggleStat = (which) => {
    setStatFilter(prev => prev === which ? null : which);
  };

  const clearAll = () => {
    setTerm("");
    setStatFilter(null);
  };

  // ---------- render ----------
  return (
    <div data-testid="tree-admin-view" style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}>
      {/* Sticky header — view-mode toggles */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          background: "var(--kc-bg)",
          padding: "var(--space-sm) 0",
          borderBottom: "1px solid var(--kc-border)",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--space-sm)",
        }}
      >
        <h1 className="text-page-title" style={{ margin: 0, flex: 1, minWidth: 160 }}>
          Follow-Up Tree
        </h1>
        <div className="flex flex-wrap" style={{ gap: "var(--space-xs)" }}>
          <ToggleButton
            active={false}
            data-testid="admin-toggle-myteam"
            onClick={onSwitchToMyTeam}
          >My Team View</ToggleButton>
          {isSuperAdmin && (
            <ToggleButton
              active={false}
              data-testid="admin-toggle-fulltree"
              onClick={onOpenFullTree}
            >Full Tree</ToggleButton>
          )}
          <ToggleButton
            active={false}
            data-testid="admin-toggle-drilldown"
            onClick={onSwitchToDrilldown}
          >Drill Down</ToggleButton>
        </div>
      </div>

      {/* Sticky search bar */}
      <div
        style={{
          position: "sticky",
          top: 60,
          zIndex: 25,
          background: "var(--kc-bg)",
          paddingBottom: "var(--space-xs)",
        }}
      >
        <div style={{ position: "relative" }}>
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              display: "inline-flex",
              color: "var(--kc-sub)",
              pointerEvents: "none",
            }}
          >
            <CustomIcon name="search" size={14} />
          </span>
          <input
            type="search"
            data-testid="tree-admin-search"
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder="Search by name, number, or slot reference"
            style={{
              width: "100%",
              padding: "10px 36px 10px 36px",
              background: "var(--kc-elevated)",
              border: "1.5px solid var(--kc-border)",
              borderRadius: "var(--kc-radius-sm)",
              color: "var(--kc-text)",
              fontFamily: "'Inter',sans-serif",
              fontSize: 13,
              outline: "none",
            }}
          />
          {term && (
            <button
              type="button"
              data-testid="tree-admin-search-clear"
              onClick={() => setTerm("")}
              aria-label="Clear search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--kc-sub)",
                padding: 6,
              }}
            >
              <CustomIcon name="close" size={12} />
            </button>
          )}
        </div>
        {trimmed && (
          <div
            data-testid="tree-admin-search-count"
            className="text-meta"
            style={{ marginTop: 6 }}
          >
            Showing {filteredCount} of {stats.total} members
          </div>
        )}
      </div>

      {/* Level selector pills */}
      <div>
        <PillRow>
          {availableLevels.map(lvl => (
            <Pill
              key={lvl}
              data-testid={`tree-level-pill-${lvl}`}
              onClick={() => focusLevel(lvl)}
              active={!!expanded[lvl]}
            >
              {nodesByLevel[lvl][0]?.levelName || `L${lvl}`}
            </Pill>
          ))}
        </PillRow>
      </div>

      {/* Quick stats bar */}
      <div
        data-testid="tree-admin-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "var(--space-xs)",
          padding: "var(--space-sm)",
          background: "var(--kc-surface)",
          border: "1px solid var(--kc-border)",
          borderRadius: "var(--kc-radius)",
        }}
      >
        <StatTile
          label="Total"
          value={stats.total}
          active={statFilter === null}
          onClick={() => setStatFilter(null)}
          testId="stat-total"
        />
        <StatTile
          label="Occupied"
          value={stats.occupied}
          active={statFilter === "occupied"}
          onClick={() => toggleStat("occupied")}
          testId="stat-occupied"
        />
        <StatTile
          label="Vacant"
          value={stats.vacant}
          active={statFilter === "vacant"}
          onClick={() => toggleStat("vacant")}
          testId="stat-vacant"
        />
        <StatTile
          label="Reassign"
          value={stats.reassign}
          active={statFilter === "reassign"}
          onClick={() => toggleStat("reassign")}
          testId="stat-reassign"
        />
        <StatTile
          label="Deletion"
          value={stats.deletion}
          active={statFilter === "deletion"}
          onClick={() => toggleStat("deletion")}
          testId="stat-deletion"
        />
      </div>

      {/* Collapsible level groups */}
      {Object.keys(filteredByLevel).length === 0 ? (
        <Card style={{ padding: "var(--space-lg)", textAlign: "center" }}>
          <div className="text-card-secondary" data-testid="tree-admin-no-results">
            No slots match the current search or filter.{" "}
            <button
              type="button"
              className="kc-inline-link"
              onClick={clearAll}
              data-testid="tree-admin-clear-all"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--kc-accent)",
                cursor: "pointer",
                textDecoration: "underline",
                padding: 0,
                font: "inherit",
              }}
            >Clear filters</button>
          </div>
        </Card>
      ) : (
        Object.keys(filteredByLevel).map(lvl => (
          <LevelGroup
            key={lvl}
            levelKey={lvl}
            nodes={filteredByLevel[lvl]}
            expanded={!!expanded[lvl]}
            onToggle={() => toggleLevel(lvl)}
            headerRef={(el) => { if (el) groupRefs.current[lvl] = el; }}
            renderCard={(n) => (
              <TreeNodeCard
                key={n.nodeId}
                node={n}
                members={members}
                term={trimmed}
                isSuperAdmin={isSuperAdmin}
                isLimitedAdmin={isLimitedAdmin}
                hasPendingDeletion={!!delReqsByNode[n.nodeId]}
                onAssign={onAssign}
                onRemove={onRemove}
                onReassign={onReassign}
                onAddChild={onAddChild}
                onViewLog={onViewLog}
                onRequestDelete={onRequestDelete}
              />
            )}
          />
        ))
      )}

      {/* Admin tool panel — collapsed by default. Sits above bottom nav via
          bottom padding on the outer page container. */}
      {isSuperAdmin && (
        <Card style={{ padding: "var(--space-sm)" }} data-testid="admin-tool-panel">
          <button
            type="button"
            data-testid="admin-tool-panel-toggle"
            onClick={() => setAdminPanelOpen(v => !v)}
            aria-expanded={adminPanelOpen}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--space-sm)",
              width: "100%",
              padding: "var(--space-sm)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "inherit",
            }}
          >
            <span className="text-eyebrow">Admin Tools</span>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                transition: "transform 0.25s ease",
                transform: adminPanelOpen ? "rotate(180deg)" : "rotate(0deg)",
                color: "var(--kc-accent)",
              }}
            >
              <Chevron size={14} />
            </span>
          </button>
          <div
            style={{
              maxHeight: adminPanelOpen ? 400 : 0,
              overflow: "hidden",
              transition: "max-height 0.25s ease",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "var(--space-sm)",
                paddingTop: "var(--space-sm)",
              }}
            >
              <ToolButton
                icon="search"
                label="Search by Slot"
                onClick={onOpenSearchPanel}
                testId="admin-tool-search"
              />
              <ToolButton
                icon="people"
                label="Tree Health"
                onClick={onOpenHealthPanel}
                testId="admin-tool-health"
              />
              <ToolButton
                icon="warning"
                label={`Reassign (${stats.reassign})`}
                onClick={onOpenReassignPanel}
                testId="admin-tool-reassign"
              />
              <ToolButton
                icon="trash"
                label={`Deletions (${stats.deletion})`}
                onClick={onOpenDeletionPanel}
                testId="admin-tool-deletion"
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// Small internal helpers — kept local because they only exist
// for this admin view.
// -------------------------------------------------------------
function ToggleButton({ active, onClick, children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-secondary btn-sm"
      data-active={active ? "true" : "false"}
      style={{
        background: active ? "var(--kc-accent-bg)" : undefined,
        color: active ? "var(--kc-accent)" : undefined,
        borderColor: active ? "var(--kc-accent)" : undefined,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

function StatTile({ label, value, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        padding: "var(--space-xs) 4px",
        background: active ? "var(--kc-accent-bg)" : "transparent",
        border: active ? "1px solid var(--kc-accent)" : "1px solid transparent",
        borderRadius: "var(--kc-radius-sm)",
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 20,
          fontWeight: 700,
          color: active ? "var(--kc-accent)" : "var(--kc-accent)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      <span className="text-eyebrow" style={{ textAlign: "center" }}>{label}</span>
    </button>
  );
}

function ToolButton({ icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-xs)",
        padding: "var(--space-sm)",
        background: "var(--kc-elevated)",
        border: "1px solid var(--kc-border)",
        borderRadius: "var(--kc-radius-sm)",
        cursor: "pointer",
        color: "var(--kc-text)",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      <span style={{ color: "var(--kc-accent)", display: "inline-flex" }}>
        <CustomIcon name={icon} size={20} />
      </span>
      <span className="text-eyebrow" style={{ textAlign: "center" }}>{label}</span>
    </button>
  );
}
